import { downloadScaleTypes, ExportFormat } from "../../utils/images/imageConvertFormats";
import { UpscaleCostProps } from "../ai/api";
import { Variant } from "../effectTheme";
import ImageProps, { LayerAreaSelection, LayerProps } from "./images";

export interface HorizontalToolbarViewportProps {
    children: React.ReactNode;
    className?: string;
};

export const ToolkitItemsHeight = '30px';
export const ToolkitLabelPosition = 'center';
export const ToolkitLabelStyle = 'text-muted';

export type BasicToolkitMode =
    | "pointer"
    | "hand"
    | "select-rect"
    | "select-oval"
    | "select-free";

export type LayerToolbarType = "draw" | "text" | "image" | null;

export interface BasicImageToolKitProps {
    activeMode: BasicToolkitMode | null;
    onChangeMode: (mode: BasicToolkitMode | null) => void;
    hasSelectedLayer: boolean;
    hasMultipleSelectedLayers?: boolean;
    selectedLayerType?: "text" | "image" | "drawings" | "ai-variation" | null;
    selectedArea: LayerAreaSelection | null;
    canUndo: boolean;
    canRedo: boolean;
    downloadFormatList: string[];
    upscaleCosts: UpscaleCostProps | null;
    colorPreviewList: string[];
    onChangeColorPreviewList: (color:string[]) => void;
    onExportImage?: (type:ExportFormat, scale: downloadScaleTypes) => void;
    isPickingTransparencyColor: boolean;
    canvasRef: any;
    image: ImageProps | null;
    layers?: LayerProps[];
    windowSizeSmall?: boolean;
    onToggleTransparencyColorPicker?: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
    onDeleteSelectedLayers: () => void;
    onCreateDrawLayer: () => void;
    onCreateTextLayer: () => void;
    onCreateImageLayer: () => void;
    onCropSelectedLayer: () => void;
    onRotateSelectedLayer: () => void;
    onFlipHorizontalSelectedLayer: () => void;
    onFlipVerticalSelectedLayer: () => void;
    onReset: () => void;
    onUndo: () => void;
    onRedo: () => void;
    onToggleCopySelectedArea: () => void;
    onToggleDeleteSelectedArea: () => void;
    onToggleCropSelectedArea: () => void;
    onToggleDuplicateSelectedLayer: () => void;
    onMergeSelectedWithPrevious: () => void;
    onMergeAllSelectedLayers: () => void;
    onExpandSelectedLayerTransparent?: () => void;
    //ImageLayer Toolkit
    selectedDrawingColor: string;
    selectedBrushSize: number;
    selectedBrushHardness: number;
    selectedBrushOpacity: number;
    selectedDrawingTool: string;
    isPickingDrawingColor: boolean;
    onChangeSelectedDrawingColor: (color: string) => void;
    onChangeSelectedBrushSize: (size: number) => void;
    onChangeSelectedBrushHardness: (hardness: number) => void;
    onChangeSelectedBrushOpacity: (opacity: number) => void;
    onChangeSelectedDrawingTool: (tool: string) => void;
    onToggleDrawingColorPicker?: () => void;
    //Textlayer toolkit
    fontTypesList?: string[];
    borderStyleList?: string[];
    borderWidthList?: number[];
    textSizesList?: number[];
    isPickingTextColor?: boolean;
    isPickingBackgroundColor?: boolean;
    isPickingBorderColor?: boolean;
    textIsBold?: boolean;
    textIsItalic?: boolean;
    textIsUnderline?: boolean;
    pickedTextColor?: string;
    pickedBorderColor?: string;
    pickedBackgroundColor?: string;
    textOpacity?: number;
    borderOpacity?: number;
    backgroundOpacity?: number;
    selectedTextSize?: number;
    selectedFontType?: string;
    selectedBorderWidth?: number;
    selectedBorderType?: string;
    onToggleTextColorPicker?: () => void;
    onToggleBackgroundColorPicker?: () => void;
    onToggleBorderColorPicker?: () => void;
    onSelectTextColor?: (color: string) => void;
    onSelectBorderColor?: (color: string) => void;
    onSelectBackgroundColor?: (color: string) => void;
    onChangeTextColorTransparency?: (alpha: number) => void;
    onChangeBorderColorTransparency?: (alpha: number) => void;
    onChangeBackgroundColorTransparency?: (alpha: number) => void;
    onSelectTextSize?: (textSize: number) => void;
    onSelectBorderWidth?: (borderWidth: number) => void;
    onSelectBorderType?: (borderType: string) => void;
    onSelectFontType?: (textFont: string) => void;
    setTextStyleIsBold?: (textStyle: boolean) => void;
    setTextStyleIsItalic?: (textStyle: boolean) => void;
    setTextStyleIsUnderline?: (textStyle: boolean) => void;
    //Image toolkit
    selectedImageLayerImage?: ImageProps | null;
    selectedImageLayerOpacity?: number;
    selectedImageLayerBorderColor?: string;
    selectedImageLayerBorderOpacity?: number;
    selectedImageLayerBorderWidth?: number;
    selectedImageLayerBorderType?: string;
    selectedImageLayerBackgroundColor?: string;
    selectedImageLayerBackgroundOpacity?: number;
    imageBorderStyleList?: string[];
    imageBorderWidthList?: number[];
    isPickingImageLayerBackgroundColor?: boolean;
    isPickingImageLayerBorderColor?: boolean;
    onSelectImageLayerImage?: (img: ImageProps) => void;
    onSelectImageLayerOpacity?: (alpha: number) => void;
    onToggleImageLayerBackgroundColorPicker?: () => void;
    onToggleImageLayerBorderColorPicker?: () => void;
    onSelectImageLayerBorderColor?: (color: string) => void;
    onSelectImageLayerBackgroundColor?: (color: string) => void;
    onChangeImageLayerBorderOpacity?: (alpha: number) => void;
    onChangeImageLayerBackgroundOpacity?: (alpha: number) => void;
    onSelectImageLayerBorderWidth?: (borderWidth: number) => void;
    onSelectImageLayerBorderType?: (borderType: string) => void;
    //History
    canUndoSelectedLayer: boolean;
    canRedoSelectedLayer: boolean;
    onUndoSelectedLayer: () => void;
    onRedoSelectedLayer: () => void;
};

export interface DrawingLayerToolbarProps {
    colorPreviewList: string[];
    selectedColor: string;
    selectedBrushSize: number;
    selectedHardness: number;
    selectedOpacity: number;
    selectedDrawingTool: string;
    isPickingDrawingColor: boolean;
    windowSizeSmall?: boolean;
    onChangeSelectedColor: (color: string) => void;
    onChangeSelectedBrushSize: (size: number) => void;
    onChangeSelectedHardness: (hardness: number) => void;
    onChangeSelectedOpacity: (opacity: number) => void;
    onChangeSelectedDrawingTool: (tool: string) => void;
    onToggleDrawingColorPicker?: () => void;
};

export interface TextLayerToolbarProps {
    fontTypesList?: string[];
    borderStyleList?: string[];
    borderWidthList?: number[];
    textSizesList?: number[];
    isPickingTextColor?: boolean;
    isPickingBackgroundColor?: boolean;
    isPickingBorderColor?: boolean;
    textIsBold?: boolean;
    textIsItalic?: boolean;
    textIsUnderline?: boolean;
    pickedTextColor?: string;
    pickedBorderColor?: string;
    pickedBackgroundColor?: string;
    textOpacity?: number;
    borderOpacity?: number;
    backgroundOpacity?: number;
    selectedTextSize?: number;
    selectedFontType?: string;
    selectedBorderWidth?: number;
    selectedBorderType?: string;
    windowSizeSmall?: boolean;
    onToggleTextColorPicker?: () => void;
    onToggleBackgroundColorPicker?: () => void;
    onToggleBorderColorPicker?: () => void;
    onSelectTextColor?: (color: string) => void;
    onSelectBorderColor?: (color: string) => void;
    onSelectBackgroundColor?: (color: string) => void;
    onChangeBorderColorTransparency?: (alpha: number) => void;
    onChangeTextColorTransparency?: (alpha: number) => void;
    onChangeBackgroundColorTransparency?: (alpha: number) => void;
    onSelectTextSize?: (textSize: number) => void;
    onSelectBorderWidth?: (borderWidth: number) => void;
    onSelectBorderType?: (borderType: string) => void;
    onSelectFontType?: (textFont: string) => void;
    setTextStyleIsBold?: (textStyle: boolean) => void;
    setTextStyleIsItalic?: (textStyle: boolean) => void;
    setTextStyleIsUnderline?: (textStyle: boolean) => void;
};

export interface ImageLayerToolkitProps {
    image?: ImageProps | null;
    imageOpacity?: number;
    borderOpacity?: number;
    borderStyleList?: string[];
    borderWidthList?: number[];
    borderColor?: string;
    borderWidth?: number;
    borderStyle?:string;
    backgroundColor?: string;
    backgroundOpacity?: number;
    isPickingBackgroundColor?: boolean;
    isPickingBorderColor?: boolean;
    pickedBorderColor?: string;
    pickedBackgroundColor?: string;
    windowSizeSmall?: boolean;
    onChangeImage?: (img: ImageProps) => void;
    onChangeImageOpacity?: (alpha: number) => void;
    onToggleBackgroundColorPicker?: () => void;
    onToggleBorderColorPicker?: () => void;
    onSelectBorderColor?: (color: string) => void;
    onSelectBackgroundColor?: (color:string) => void;
    onChangeBorderColorTransparency?: (alpha:number) => void;
    onChangeBackgroundColorTransparency?: (alpha:number) => void;
    onSelectBorderWidth?: (borderWidth: number) => void;
    onSelectBorderType?: (borderType:string) => void;
};

export type ToolbarPortalDropdownProps = {
    button: React.ReactNode;
    children: React.ReactNode;
    menuWidth?: number;
    variant?: Variant;
    title?: string;
    maxHeight?: string | number;
    buttonClassname?: string;
};

export type ToolbarPortalDropdownItemProps = {
    children: React.ReactNode;
    onClick?: () => void;
    active?: boolean;
    className?: string;
    style?: React.CSSProperties;
};